import { Icon, SelectionMode, Stack } from "@fluentui/react";
import * as React from "react";
import { Section } from "../ui/atoms/section";

import sizes from "../ui/sizes.scss";
import { ListVehicles, ListVehiclesLayout } from "../ui/molecules/list-vehicles";
import type {
    CostInformation,
    ExpiringLeaseTerms,
    ProcurementTypeCount,
    VehicleFleetStatistics,
} from "../api";
import { ProcurementType } from "../api";
import colors from "./../ui/colors.scss";
import dashboardColors from "./dashboard-colors.scss";
import type { PieLabelRenderProps } from "recharts";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useEffect, useState } from "react";
import { store } from "../store";
import { DatePicker, DateType } from "../ui/organisms/date-picker";
import { endOfMonth, startOfMonth } from "date-fns";
import css from "./page-fleet-statistics.scss";
import { FusionLayout } from "@/parts/fusion-layout";

const chartContentHeight = Number(sizes.pageFleetStatisticsChartSectionHeight) - 77;

export function PageFleetStatistics(_props: {}): JSX.Element {
    const [fleetStatistics, setFleetStatistics] = useState<VehicleFleetStatistics>();
    const [costsPeriodStart, setCostsPeriodStart] = useState(startOfMonth(new Date()));
    const [costsPeriodEnd, setCostsPeriodEnd] = useState(endOfMonth(new Date()));

    useEffect(() => {
        store.vehiclesApi
            .vehiclesReadVehicleCostInformation({ costsPeriodStart, costsPeriodEnd })
            .then(setFleetStatistics);
    }, [costsPeriodStart, costsPeriodEnd]);

    function onDateRangeChanged(dateStart?: Date, dateEnd?: Date): void {
        if (dateStart) {
            setCostsPeriodStart(dateStart);
        }
        if (dateEnd) {
            setCostsPeriodEnd(dateEnd);
        }
    }

    return (
        <FusionLayout breadcrumb={store.i18n.t("page.fleetStatistics.title")} classic>
            <Stack>
                {fleetStatistics && (
                    <div className={css.pageFleetStatistics__chartGrid}>
                        <Section
                            className={css.pageFleetStatistics__fullWidthItem}
                            withPadding
                            title={store.i18n.t(
                                "page.fleetStatistics.charts.vehicleCostByProcurementType.sectionTitle",
                            )}
                        >
                            {VehicleCostByProcurementTypePieChart(
                                onDateRangeChanged,
                                fleetStatistics.costInformation,
                            )}
                        </Section>
                        <Section
                            withPadding
                            title={store.i18n.t(
                                "page.fleetStatistics.charts.vehicleCountByProcurementType.sectionTitle",
                            )}
                        >
                            {VehicleCountByProcurementTypePieChart(
                                fleetStatistics.procurementTypeCounts,
                            )}
                        </Section>
                        <Section
                            withPadding
                            title={store.i18n.t(
                                "page.fleetStatistics.charts.expiringLeaseTerms.sectionTitle",
                            )}
                        >
                            {ExpiringLeaseTermsPieChart(fleetStatistics.expiringLeaseTerms)}
                        </Section>
                        <Section
                            withPadding
                            title={store.i18n.t(
                                "page.fleetStatistics.charts.totalTrucks.sectionTitle",
                            )}
                        >
                            {TotalTrucksBarChart(
                                fleetStatistics.totalElofleetVehicles,
                                fleetStatistics.totalNonElofleetVehicles,
                            )}
                        </Section>
                    </div>
                )}
                <Section>
                    <ListVehicles
                        selectionMode={SelectionMode.none}
                        columnLayout={ListVehiclesLayout.FleetStatistics}
                        maxHeight={`calc(${sizes.listMaxHeight} - ${sizes.gutter} * 2 - ${sizes.pageFleetStatisticsChartSectionHeight}px)`}
                        hideExport
                    />
                </Section>
            </Stack>
        </FusionLayout>
    );
}

const PROCUREMENT_TYPE_COLORS: Map<ProcurementType, string> = new Map([
    [ProcurementType.LEASING, colors.costInformationleased],
    [ProcurementType.RENTAL, colors.costInformationrented],
    [ProcurementType.PURCHASE, colors.costInformationpurchased],
]);

function VehicleCountByProcurementTypePieChart(
    procurementTypeCounts: ProcurementTypeCount[],
): JSX.Element {
    const countData = procurementTypeCounts.map((count) => {
        return {
            name: store.i18n.t(`listVehicles.procurementType.${count.procurementType}`),
            value: count.count,
            procurementType: count.procurementType,
        };
    });

    return (
        <Stack horizontalAlign="center">
            <ResponsiveContainer width={"100%"} height={chartContentHeight}>
                <PieChart className={css.pageFleetStatistics__pieChart}>
                    <Pie
                        data={countData}
                        cx={"50%"}
                        cy={"50%"}
                        dataKey="value"
                        label={renderPercentageLabel}
                        labelLine={false}
                    >
                        {countData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={PROCUREMENT_TYPE_COLORS.get(entry.procurementType)}
                                style={{
                                    filter: `drop-shadow(0px 0px 5px ${PROCUREMENT_TYPE_COLORS.get(entry.procurementType)}`,
                                }}
                            />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
        </Stack>
    );
}

function VehicleCostByProcurementTypePieChart(
    onDateRangeChanged: (dateStart?: Date, dateEnd?: Date) => void,
    costInformation: CostInformation[],
): JSX.Element {
    const costData = costInformation.map((info) => {
        return {
            name: store.i18n.t(`listVehicles.procurementType.${info.procurementType}`),
            value: info.costs,
            procurementType: info.procurementType,
        };
    });

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack
                horizontal
                style={{ width: "100%" }}
                horizontalAlign="end"
                verticalAlign="center"
                tokens={{ childrenGap: 20 }}
            >
                <DatePicker
                    onDateRangeChanged={onDateRangeChanged}
                    initialDateType={DateType.MONTH}
                    allowedDateTypes={[DateType.MONTH, DateType.YEAR]}
                />
            </Stack>
            <Stack horizontalAlign="center">
                {/** Height is `chartContentHeight` minus the height of the date picker.*/}

                {costData.filter((item) => item.value == 0).length == costData.length ? (
                    <Stack
                        style={{ height: chartContentHeight - 60 }}
                        horizontalAlign="center"
                        verticalAlign="center"
                    >
                        <Icon iconName="SearchIssue" styles={{ root: { fontSize: "2rem" } }} />
                        {store.i18n.t("page.fleetStatistics.charts.emptyData.label")}
                    </Stack>
                ) : (
                    <ResponsiveContainer width={"100%"} height={chartContentHeight - 60}>
                        <PieChart className={css.pageFleetStatistics__pieChart}>
                            <Pie
                                data={costData}
                                cx={"50%"}
                                cy={"50%"}
                                dataKey="value"
                                label={(props) =>
                                    renderPercentageLabel(props, (costs) =>
                                        store.i18n.formatCurrency(costs),
                                    )
                                }
                                labelLine={false}
                            >
                                {costData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={PROCUREMENT_TYPE_COLORS.get(entry.procurementType)}
                                        style={{
                                            filter: `drop-shadow(0px 0px 5px ${PROCUREMENT_TYPE_COLORS.get(entry.procurementType)}`,
                                        }}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </Stack>
        </Stack>
    );
}

export enum LeaseContractsExpirationThreshold {
    SIX_MONTHS = "sixMonths",
    ONE_YEAR = "oneYear",
    TWO_YEARS = "twoYears",
}

const EXPIRING_LEASE_CONSTRACTS_COLORS: Map<LeaseContractsExpirationThreshold, string> = new Map([
    [LeaseContractsExpirationThreshold.SIX_MONTHS, dashboardColors.impactChartSevere],
    [LeaseContractsExpirationThreshold.ONE_YEAR, dashboardColors.impactChartMedium],
    [LeaseContractsExpirationThreshold.TWO_YEARS, dashboardColors.impactChartLight],
]);

function ExpiringLeaseTermsPieChart(expiringLeaseTerms: ExpiringLeaseTerms): JSX.Element {
    const leaseTermData = [
        {
            name: store.i18n.t(
                `page.fleetStatistics.charts.expiringLeaseTerms.${LeaseContractsExpirationThreshold.SIX_MONTHS}`,
            ),
            value: expiringLeaseTerms.inSixMonths,
            threshold: LeaseContractsExpirationThreshold.SIX_MONTHS,
        },
        {
            name: store.i18n.t(
                `page.fleetStatistics.charts.expiringLeaseTerms.${LeaseContractsExpirationThreshold.ONE_YEAR}`,
            ),
            value: expiringLeaseTerms.inOneYear,
            threshold: LeaseContractsExpirationThreshold.ONE_YEAR,
        },
        {
            name: store.i18n.t(
                `page.fleetStatistics.charts.expiringLeaseTerms.${LeaseContractsExpirationThreshold.TWO_YEARS}`,
            ),
            value: expiringLeaseTerms.inTwoYears,
            threshold: LeaseContractsExpirationThreshold.TWO_YEARS,
        },
    ];
    return (
        <Stack horizontalAlign="center">
            <ResponsiveContainer width={"100%"} height={chartContentHeight}>
                <PieChart className={css.pageFleetStatistics__pieChart}>
                    <Pie
                        data={leaseTermData}
                        cx={"50%"}
                        cy={"50%"}
                        dataKey="value"
                        label={renderPercentageLabel}
                        labelLine={false}
                    >
                        {leaseTermData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={EXPIRING_LEASE_CONSTRACTS_COLORS.get(entry.threshold)}
                                style={{
                                    filter: `drop-shadow(0px 0px 5px ${EXPIRING_LEASE_CONSTRACTS_COLORS.get(entry.threshold)}`,
                                }}
                            />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
        </Stack>
    );
}

function TotalTrucksBarChart(
    totalElofleetTrucks: number,
    totalNonElofleetTrucks: number,
): JSX.Element {
    const countData = [
        {
            name: store.i18n.t("page.fleetStatistics.charts.totalTrucks.sectionTitle"),
            totalElofleetTrucks: totalElofleetTrucks,
            totalNonElofleetTrucks: totalNonElofleetTrucks,
        },
    ];

    return (
        <Stack horizontalAlign="center">
            <ResponsiveContainer width={"100%"} height={chartContentHeight}>
                <BarChart className={css.pageFleetStatistics__pieChart} data={countData}>
                    <Tooltip />
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Bar
                        dataKey="totalElofleetTrucks"
                        name={store.i18n.t(
                            "page.fleetStatistics.charts.totalTrucks.elofleetTrucks",
                        )}
                        fill={colors.costInformationleased}
                        background={{ opacity: 0.5 }}
                    >
                        <LabelList
                            formatter={() =>
                                store.i18n.t(
                                    "page.fleetStatistics.charts.totalTrucks.elofleetTrucks",
                                )
                            }
                            position="top"
                        />
                    </Bar>
                    <Bar
                        dataKey="totalNonElofleetTrucks"
                        name={store.i18n.t(
                            "page.fleetStatistics.charts.totalTrucks.nonElofleetTrucks",
                        )}
                        fill={colors.costInformationrented}
                        background={{ opacity: 0.5 }}
                    >
                        <LabelList
                            formatter={() =>
                                store.i18n.t(
                                    "page.fleetStatistics.charts.totalTrucks.nonElofleetTrucks",
                                )
                            }
                            position="top"
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Stack>
    );
}

const RADIAN = Math.PI / 180;

/** Extracts the percentage value of the pie chart category and adds it to the
    label name. Takes in an optional formatter. */
function renderPercentageLabel(
    { percent, value, name, cx, cy, midAngle, innerRadius, outerRadius }: PieLabelRenderProps,
    formatter?: (value: number) => string,
): JSX.Element {
    if (!percent) {
        return <></>;
    }
    let label = String(value);
    if (formatter) {
        label = formatter(value);
    }
    label = `${label} (${(percent * 100).toFixed(1)}%)`;
    const radius = 25 + innerRadius + (outerRadius - innerRadius);
    const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

    const textWidth = 8 * label.length; // Adjust this to dynamically calculate based on text content
    const textHeight = 45; // Adjust this to fit your needs
    const padding = 5;

    return (
        <g>
            {/* Background rectangle */}
            <rect
                x={x - textWidth / 2}
                y={y - textHeight / 2 - padding}
                width={textWidth}
                height={textHeight}
                fill="white"
                stroke="lightgrey"
                strokeWidth={1}
            />
            <text x={x} y={y - 10} textAnchor="middle" fill="rgb(65, 65, 64)">
                {label}
                <tspan x={x} y={y + 10}>
                    {name}
                </tspan>
            </text>
        </g>
    );
}
